import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { AdminOnly } from '@/components/AdminOnly'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useBulkCreateCohorts } from '@/hooks/useBulkCreateCohorts'
import { useDeleteCohortsByDistrict } from '@/hooks/useDeleteCohortsByDistrict'
import { useCohortsByDistrict } from '@/hooks/useCohortsByDistrict'
import { generateCohortsForDistrict } from '@/lib/cohortGenerator'
import type { RegionType, SettlementType } from '@/types'

interface GenerateCohortsButtonProps {
  districtId: string
  districtName: string
  districtPopulation: number
  settlementType: SettlementType
  regionType: RegionType
  variant?: 'default' | 'icon'
}

export function GenerateCohortsButton({
  districtId,
  districtName,
  districtPopulation,
  settlementType,
  regionType,
  variant = 'default',
}: GenerateCohortsButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { cohorts, isLoading: cohortsLoading } = useCohortsByDistrict(districtId)
  const { bulkCreateCohortsAsync } = useBulkCreateCohorts()
  const { deleteCohortsByDistrictAsync } = useDeleteCohortsByDistrict()

  const existingCohortCount = cohorts?.length ?? 0

  const handleGenerate = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      // Delete existing cohorts if any
      if (existingCohortCount > 0) {
        await deleteCohortsByDistrictAsync(districtId)
      }

      // Generate new cohorts
      const generatedCohorts = generateCohortsForDistrict({
        districtId,
        districtPopulation,
        settlementType,
        regionType,
      })

      // Bulk insert the cohorts
      await bulkCreateCohortsAsync(generatedCohorts)

      setSuccessMessage(`Generated ${generatedCohorts.length} cohorts`)
      setIsDialogOpen(false)
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate cohorts')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    if (!isProcessing) {
      setIsDialogOpen(false)
      setError(null)
    }
  }

  return (
    <AdminOnly>
      <div className="flex items-center gap-2">
        {variant === 'icon' ? (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsDialogOpen(true)}
            disabled={cohortsLoading}
            title="Generate Cohorts"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsDialogOpen(true)}
            disabled={cohortsLoading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Generate Cohorts
          </Button>
        )}

        {successMessage && <span className="text-sm text-green-600">{successMessage}</span>}

        <AlertDialog open={isDialogOpen} onOpenChange={(open) => !open && handleCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate Cohorts</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  Generate voter cohorts for "{districtName}" district?
                </span>
                {existingCohortCount > 0 && (
                  <span className="block font-medium text-amber-600">
                    This will replace {existingCohortCount} existing{' '}
                    {existingCohortCount === 1 ? 'cohort' : 'cohorts'}.
                  </span>
                )}
                {error && <span className="block text-red-600">{error}</span>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleGenerate} disabled={isProcessing}>
                {isProcessing ? 'Generating...' : 'Generate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminOnly>
  )
}
